// src/App.jsx
import React, { useEffect, useState } from 'react'
import { Routes, Route } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from './hooks/useAppDispatch.js'
import { fetchGlobalConfig } from './redux/slices/globalComponentsSlice.js'
import { initializeAuth } from './redux/slices/authSlice.js'
import PublicRoutes from './routes/PublicRoutes.jsx'
import AdminRoutes from './routes/AdminRoutes.jsx'
import { ErrorBoundary } from './components/common/ErrorBoundary.jsx'
import RouteChangeTracker from './analytics/RouteChangeTracker'

function App() {
    const dispatch = useAppDispatch()
    const { loading: globalLoading } = useAppSelector((state) => state.global)
    const [minimumLoading, setMinimumLoading] = useState(true)

    useEffect(() => {
        // Initialize auth state from localStorage
        dispatch(initializeAuth())
        // Then fetch global config
        dispatch(fetchGlobalConfig())

        // Minimum loading time of 500ms to prevent jitter
        const timer = setTimeout(() => {
            setMinimumLoading(false)
        }, 1000)

        return () => clearTimeout(timer)
    }, [dispatch])

    // Handle WhatsApp click
    const handleWhatsAppClick = () => {
        window.open('https://wa.me/7880122101', '_blank');
    };

    // Show loader if global data is loading OR minimum loading time hasn't passed
    if (globalLoading || minimumLoading) {
        return (
            <div className="page-loader min-h-screen bg-black flex flex-col items-center justify-center">
                <div style={{ display: "flex", gap: "5px" }} className="flex mt-8 space-x-2">
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: "var(--primary)" }}
                            animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: i * 0.2,
                                ease: "easeInOut"
                            }}
                        />
                    ))}
                </div>
            </div>
        )
    }

    return (
        <ErrorBoundary>
            <RouteChangeTracker />
            <div className="App">
                <Routes>
                    <Route path="/*" element={<PublicRoutes />} />
                    <Route path="/admin/*" element={<AdminRoutes />} />
                </Routes>

                {/* Floating WhatsApp Button */}
                <motion.button
                    onClick={handleWhatsAppClick}
                    className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full overflow-hidden focus:outline-none p-1 hover:shadow-xl"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                        duration: 0.3
                    }}
                >
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                        alt="WhatsApp"
                        className="w-full h-full object-cover"
                    />
                </motion.button>
            </div>
        </ErrorBoundary>
    )
}

export default App